import { ConfigProvider, Layout, Menu, theme } from 'antd';
import { ENV_NAME, themeConfig } from 'config/env-config';
import useApps from 'hooks/use-app';
import { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';

const isProd = ENV_NAME === 'production';

// `extra` renders below the menu, e.g. the agent app's chat threads.
const SideMenu = ({ items, extra = null }) => {
  const { pathname } = useLocation();
  const { urlKey } = useApps();
  // Lazy initialize state based on urlKey
  const [openKeys, setOpenKeys] = useState(() =>
    urlKey !== 'home' ? [urlKey] : []
  );
  const [menuItems, setMenuItems] = useState([]);

  // Memoize URL path segments
  const urlItems = useMemo(() => pathname.split('/').filter(Boolean), [
    pathname,
  ]);

  // Keep openKeys in sync with urlKey
  useEffect(() => {
    setOpenKeys(urlKey !== 'home' ? [urlKey] : []);
  }, [urlKey]);

  // Update menu items based on current URL section
  useEffect(() => {
    if (urlKey === 'home' || !urlItems.length) {
      setMenuItems([]);
      return;
    }

    const selectedSection = urlItems[0];

    setMenuItems(items);
  }, [urlItems, urlKey]);

  // Generate path segments for selected keys
  const selectedKeys = useMemo(() => {
    if (!urlItems.length) return [];

    // Build array of path segments for menu selection
    return urlItems.reduce((paths, _, index) => {
      const currentPath = '/' + urlItems.slice(0, index + 1).join('/');
      return [...paths, currentPath];
    }, []);
  }, [urlItems]);

  // Create theme configuration based on environment
  const customTheme = useMemo(() => {
    const baseTheme = { algorithm: theme.darkAlgorithm };

    if (isProd) return baseTheme;

    return {
      ...baseTheme,
      token: {
        colorPrimaryBg: themeConfig.primaryColor,
      },
      components: {
        Menu: {
          itemBg: themeConfig.backgroundColor,
          subMenuItemBg: themeConfig.backgroundColor,
          groupTitleColor: themeConfig.groupTitleColor,
          itemSelectedColor: themeConfig.itemSelectedColor,
        },
      },
    };
  }, []);

  return (
    <ConfigProvider theme={customTheme}>
      <Layout.Sider
        breakpoint="xl"
        collapsedWidth="0"
        style={
          !isProd ? { backgroundColor: themeConfig.backgroundColor } : undefined
        }
      >
        <Menu
          theme={!isProd ? undefined : 'dark'}
          mode="inline"
          openKeys={openKeys}
          onOpenChange={setOpenKeys}
          selectedKeys={selectedKeys}
          items={menuItems}
        />
        {extra}
      </Layout.Sider>
    </ConfigProvider>
  );
};

export default SideMenu;
